import React, {
  createContext,
  FC,
  ReactNode,
  Context,
  useState,
  useEffect,
  useMemo,
} from "react";
import { IAppRoute } from "@App/types";
import {
  mergeNavigateProps,
  NavigateProps,
} from "@Libs/Router/Components/Navigate/Navigate";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";

type GenericFlowProps<OnSuccessProps, State extends object> = {
  root?: IAppRoute;
  onSuccess?: (props: OnSuccessProps) => void;
  onLeave?: () => void;
  onError?: (error: Error) => void;
  backRoute: NavigateProps;
  onPageMount?: (route: IAppRoute) => void;
  state?: State;
  setState?: (state: Partial<State>) => void;
  rootRoute?: NavigateProps;
  navigateToStep?: (step: IAppRoute, options?: NavigateProps) => void;
};

type ProviderProps<OnSuccessProps, State extends object> = GenericFlowProps<
  OnSuccessProps,
  State
> & {
  root?: undefined;
};

export const createFlow = <
  OnSuccessProps = undefined,
  State extends object = object
>(
  initialProps?: Partial<GenericFlowProps<OnSuccessProps, State>> & {
    root: IAppRoute;
  }
): {
  context: Context<GenericFlowProps<OnSuccessProps, State>>;
  Provider: FC<ProviderProps<OnSuccessProps, State> & { children: ReactNode }>;
  root: IAppRoute;
} => {
  const FlowContext = createContext<GenericFlowProps<OnSuccessProps, State>>({
    onSuccess: () => undefined,
    onError: () => undefined,
    backRoute: { to: ["ROOT"] },
    onPageMount: () => undefined,
    state: {} as State,
    setState: () => undefined,
    root: "ROOT",
    ...initialProps,
  });

  const FlowProvider: FC<
    ProviderProps<OnSuccessProps, State> & { children: ReactNode }
  > = ({ children, ...props }) => {
    const navigate = useNavigate();

    const initialState = useMemo(
      () => ({
        ...initialProps?.state,
        ...props.state,
      }),
      [props.state]
    );

    const [state, setStateHook] = useState<State>(initialState);

    if (!props.backRoute) {
      throw new Error("You must pass backRoute prop when rendering your flow");
    }

    const rootRoute = mergeNavigateProps(props.backRoute, {
      to: initialProps.root,
    });

    const navigateToStep = (step: IAppRoute, options?: NavigateProps) => {
      const props = mergeNavigateProps(rootRoute, { ...options, to: step });
      navigate(props.to, props);
    };

    useEffect(() => {
      setStateHook(initialState);
    }, [initialState]);

    const setState = (state: Partial<State>) => {
      setStateHook((prevState) => ({ ...prevState, ...state }));
    };

    return (
      <FlowContext.Provider
        value={{
          ...props,
          root: initialProps.root,
          state,
          setState,
          rootRoute,
          navigateToStep,
        }}
      >
        {children}
      </FlowContext.Provider>
    );
  };

  return {
    context: FlowContext,
    Provider: FlowProvider,
    root: initialProps.root,
  };
};

export type IFlow<T, S extends object> = ReturnType<typeof createFlow<T, S>>;
